/**
 * 虚拟资源树节点：文件夹包含子节点；文件节点用 links[0] 存项目资源 URL。
 * 当前约定：
 * `links[1]` 保存素材库原始下载地址；
 * `links[2]` 保存字体显示名等附加元信息；
 * `links[3]` 保存立绘合成图组合签名；
 * `links[4]` 保存事件属性使用的相对路径；
 * `links[5]` 保存资源映射路径（如 Graphics/background）。
 * `links[6]` 保存资源 md5；
 * `links[7]` 保存资源字节大小；
 * 与 ResourceBrowser 绑定：图像分类下用 links[0] 直接作为缩略图 src。
 */
export type DataFileMapJson = {
  name: string
  isFolder: boolean
  children?: DataFileMapJson[]
  links?: string[]
}

export default class DataFileMap {
  public name: string = ''
  public isFolder: boolean = false
  public children: DataFileMap[] = []
  /** 文件节点至少应含一项：资源地址（http(s) / data: 等） */
  public links: string[] = []

  get resourceUrl(): string {
    return this.links[0]?.trim() ?? ''
  }

  set resourceUrl(value: string) {
    this.links[0] = value.trim()
  }

  get relativePath(): string {
    return this.links[4]?.trim() ?? ''
  }

  set relativePath(value: string) {
    this.links[4] = value.trim()
  }

  get mappingPath(): string {
    return this.links[5]?.trim() ?? ''
  }

  set mappingPath(value: string) {
    this.links[5] = value.trim()
  }

  get resourceMd5(): string {
    return this.links[6]?.trim().toLowerCase() ?? ''
  }

  set resourceMd5(value: string) {
    this.links[6] = value.trim().toLowerCase()
  }

  get resourceSize(): number {
    const value = Number(this.links[7] ?? 0)
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
  }

  set resourceSize(value: number) {
    this.links[7] = Number.isFinite(value) && value > 0 ? String(Math.floor(value)) : ''
  }

  constructor(json?: object | null) {
    if (json != null) this.fromJson(json)
  }

  fromJson(json: object): void {
    const o = json as Partial<DataFileMapJson>
    this.name = typeof o.name === 'string' ? o.name : ''
    this.isFolder = !!o.isFolder
    const rawChildren = o.children
    this.children = Array.isArray(rawChildren)
      ? rawChildren.map((c) => new DataFileMap(c as object))
      : []
    const rawLinks = o.links
    this.links = Array.isArray(rawLinks)
      ? rawLinks.map((value) => typeof value === 'string' ? value : '')
      : []
  }

  toJson(): DataFileMapJson {
    return {
      name: this.name,
      isFolder: this.isFolder,
      children: this.children.map((c) => c.toJson()),
      links: Array.from(
        { length: this.links.length },
        (_, index) => typeof this.links[index] === 'string' ? this.links[index] : '',
      ),
    }
  }

  addChild(child: DataFileMap): void {
    this.children.push(child)
  }

  /** 与磁盘分类根目录（如 graphics）对应的空文件夹 */
  static categoryRoot(displayName: string): DataFileMap {
    const n = new DataFileMap()
    n.name = displayName
    n.isFolder = true
    return n
  }

  /** 叶子：显示名 + 资源 URL */
  static fileLeaf(displayName: string, url: string): DataFileMap {
    const n = new DataFileMap()
    n.name = displayName
    n.isFolder = false
    n.resourceUrl = url
    return n
  }

  /**
   * 在本节点子树中查找文件夹；path 为相对当前节点的路径段，'' 表示当前节点。
   */
  findFolderByRelativePath(path: string): DataFileMap | null {
    const segments = path.split('/').filter(Boolean)
    let cur: DataFileMap = this
    for (const seg of segments) {
      const next = cur.children.find((c) => c.isFolder && c.name === seg)
      if (!next) return null
      cur = next
    }
    return cur
  }
}
